/**
 * The wire names the two halves of the tenant hand-off share (customer onboarding S5, WS-15, R-062
 * as R-064 item 1 amends it): the page on the tenant host that starts the hand-off
 * (`requestTenantHandoff` in `tenant-handoff.ts`) writes the record, the DP host page
 * (`beginTenantHandoff`) writes the callback URL, and the shell boot on the tenant host
 * (`redeemTenantHandoff` in the shell's `src/tenant/tenant-handoff-callback.ts`) reads both. The
 * package exports this module as `@zion/shell-system-surfaces/tenant-handoff-wire`.
 *
 * The tenant tab keeps the PKCE verifier and the state in its own `sessionStorage`, as the kernel
 * hand-off keeps its record on the redeeming origin. The callback URL carries the code and the
 * state alone. So a code and a URL from another browser redeem in no tab (WS-15-P2-R1, R-064 item 1).
 *
 * Constants and types only: no I/O, no state, nothing that throws.
 */

/** The page on the tenant host that redeems a hand-off code. It sits under the reserved `auth` segment. */
export const TENANT_HANDOFF_CALLBACK_PATH = '/auth/handoff-callback' as const;

/**
 * The fragment names of the callback URL, each once, in this order, and no other name: the
 * one-time code and the state of the tenant tab's record. A browser sends no fragment to a server.
 */
export const TENANT_HANDOFF_FRAGMENT_PARAMS = Object.freeze(['code', 'state'] as const);

/** The longest callback fragment, `#` included, in characters: 2,560. */
export const MAX_TENANT_CALLBACK_FRAGMENT_LENGTH = 2560 as const;

/**
 * The query names the tenant host adds to the DP host login URL, beside `orgSlug` and `returnTo`:
 * the S256 challenge of the tenant tab's verifier, and the tab's state.
 */
export const TENANT_HANDOFF_LOGIN_PARAMS = Object.freeze(['handoffChallenge', 'handoffState'] as const);

/** The `sessionStorage` key of the one hand-off record of a tenant host tab. */
export const TENANT_HANDOFF_STORAGE_KEY = 'zion.tenant-handoff' as const;

/** How long a hand-off record lives, in milliseconds: 600,000 (ten minutes), as the kernel record. */
export const TENANT_HANDOFF_TTL_MS = 600_000 as const;

/**
 * The record a tenant host tab keeps, as JSON under {@link TENANT_HANDOFF_STORAGE_KEY}, between the
 * start of a hand-off and its callback.
 *
 * - `v`: `1`.
 * - `state`: 22 base64url characters; it passes `KERNEL_HANDOFF_STATE_PATTERN`.
 * - `verifier`: 43 base64url characters; it passes `PKCE_CODE_VERIFIER_PATTERN`.
 * - `returnTo`: the landing after the redeem; it passes `KERNEL_RETURN_PATH_PATTERN`.
 * - `createdAt`: the epoch milliseconds of the write.
 */
export interface TenantHandoffRecord {
  readonly v: 1;
  readonly state: string;
  readonly verifier: string;
  readonly returnTo: string;
  readonly createdAt: number;
}
